import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

export const DISCLOSURE_DURATION = 240;

export default function Disclosure({ open, id, labelledBy, className = '', children }) {
  const [present, setPresent] = useState(open);
  const [height, setHeight] = useState(0);
  const body = useRef();
  const measurable = typeof ResizeObserver === 'function';

  useLayoutEffect(() => {
    if (!open) { setHeight(0); return; }
    if (!present) { setPresent(true); return; }
    if (!measurable) return;
    let observing = true;
    const measure = () => {
      if (observing && body.current) {
        const outer = body.current.parentElement;
        let target = body.current.getBoundingClientRect().height;
        // Follow nested endpoints, not their intermediate frames: both levels
        // must finish together rather than continually restarting the parent.
        body.current.querySelectorAll('.portfolio-disclosure').forEach((panel) => {
          if (panel.parentElement.closest('.portfolio-disclosure') !== outer) return;
          const endpoint = Number.parseFloat(panel.style.height);
          if (Number.isFinite(endpoint)) target += endpoint - panel.getBoundingClientRect().height;
        });
        setHeight(Math.max(0, Math.ceil(target)));
      }
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(body.current);
    const element = body.current;
    element.addEventListener('portfolio-panel-size', measure);
    return () => {
      observing = false; observer.disconnect();
      element.removeEventListener('portfolio-panel-size', measure);
    };
  }, [open, present, measurable]);

  useLayoutEffect(() => {
    body.current?.parentElement.dispatchEvent(new Event('portfolio-panel-size', { bubbles: true }));
  }, [height, present]);

  useEffect(() => {
    if (open || !present) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) {
      setPresent(false); return;
    }
    // A detached tab or interrupted transition may never deliver transitionend.
    const timer = setTimeout(() => setPresent(false), DISCLOSURE_DURATION + 40);
    return () => clearTimeout(timer);
  }, [open, present]);

  return <div id={id} role="region" aria-labelledby={labelledBy}
    className={`portfolio-disclosure ${className}`} hidden={!present}
    aria-hidden={!open} inert={open ? undefined : ''}
    data-open={open} data-present={present}
    style={{ height: open && !measurable ? 'auto' : height }}
    onTransitionEnd={(event) => {
      if (!open && event.target === event.currentTarget && event.propertyName === 'height') {
        setPresent(false);
      }
    }}>
    <div ref={body} className="portfolio-disclosure-body">
      {present && children(open)}
    </div>
  </div>;
}
